import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle,
  Tabs,
  Tab,
  Paper,
  Stack,
  CircularProgress,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AbsenceForm from '../components/absences/AbsenceForm';
import AbsencesList from '../components/absences/AbsencesList';
import AbsenceCalendar from '../components/absences/AbsenceCalendar';
import AbsenceImportExport from '../components/absences/AbsenceImportExport';
import LiisaImport from '../components/absences/LiisaImport';
import PageTitle from '../components/layout/PageTitle';
import { useTeamPermissions } from '../hooks/useTeamPermissions';
import { useAbsences } from '../contexts/AbsencesContext';
import { useLocation } from 'react-router-dom';

const AbsencesPage = () => {
  const location = useLocation();
  const [openForm, setOpenForm] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const { canAdd, canEdit, canDelete } = useTeamPermissions();
  const { loading, error, loadAbsences, absences } = useAbsences();

  // Gestion des paramètres de navigation depuis le dialog de conflit
  useEffect(() => {
    if (location.state?.editAbsenceId && location.state?.openForm) {
      const absenceToEdit = absences.find(abs => abs.id === location.state.editAbsenceId);
      if (absenceToEdit) {
        setSelectedAbsence(absenceToEdit);
        setOpenForm(true);
        // Nettoyer le state pour éviter la réouverture
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [location.state, absences]);

  const handleOpenForm = (absence = null) => {
    setSelectedAbsence(absence);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedAbsence(null);
  };

  const handleSuccess = () => {
    setRefreshKey(prev => prev + 1);
    loadAbsences(true);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle
        title="Gestion des Absences"
        actions={
          canAdd('absences') && (
            <Stack direction="row" spacing={2}>
              <AbsenceImportExport onSuccess={handleSuccess} />
              <LiisaImport onSuccess={handleSuccess} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenForm()}
              >
                Ajouter une absence
              </Button>
            </Stack>
          )
        }
      />

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Calendrier" />
          <Tab label="Liste" />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {currentTab === 0 ? (
          <AbsenceCalendar
            key={`calendar-${refreshKey}`}
            onEdit={(canEdit('absences') || canAdd('absences')) ? handleOpenForm : null}
          />
        ) : (
          <AbsencesList 
            key={`list-${refreshKey}`}
            onEdit={(canEdit('absences') || canAdd('absences')) ? handleOpenForm : null}
          />
        )}
      </Box>

      {(canAdd('absences') || canEdit('absences')) && (
        <Dialog 
          open={openForm} 
          onClose={handleCloseForm}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedAbsence ? 'Modifier une absence' : 'Ajouter une absence'}
          </DialogTitle>
          <AbsenceForm
            absence={selectedAbsence}
            onClose={handleCloseForm}
            onSuccess={handleSuccess}
          />
        </Dialog>
      )}
    </Box>
  );
};

export default AbsencesPage;
